import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiMessageSquare, FiPlus, FiX, FiImage } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];


const EmployeeTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    project: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const LIMIT = 10;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    project_id: '',
    assigned_users: [],
    due_date: ''
  });
  const [modalProjectSearch, setModalProjectSearch] = useState('');
  const [modalProjectOpen, setModalProjectOpen] = useState(false);
  const [modalSelectedProject, setModalSelectedProject] = useState(null);
  const [modalUserSearch, setModalUserSearch] = useState('');
  const [modalSelectedUsers, setModalSelectedUsers] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'project'
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [uploadingComment, setUploadingComment] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [descPopup, setDescPopup] = useState(null);

  useEffect(() => {
    fetchTasks(1);
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchTasks = async (page = 1, activeFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: LIMIT };
      // Always exclude completed unless explicitly filtering for them
      if (activeFilters.status) {
        params.status = activeFilters.status;
      } else {
        params.status = 'active';
      }
      if (activeFilters.priority) params.priority = activeFilters.priority;
      if (activeFilters.project) params.project_name = activeFilters.project;

      const response = await api.post('/employee/tasks/list', params);
      const data = response.data;

      let allTasks = data.tasks || data || [];
      const sortedTasks = allTasks.sort((a, b) => {
        const order = { todo: 1, in_progress: 2, testing: 3, client_review: 4, completed: 5 };
        return (order[a.status] || 6) - (order[b.status] || 6);
      });

      setTasks(sortedTasks);
      setFilteredTasks(sortedTasks);
      if (data.pagination) {
        setPagination(data.pagination);
        setCurrentPage(page);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      if (err.response && err.response.status !== 404) {
        setError('Failed to load tasks. Please try again.');
      } else {
        setTasks([]);
        setFilteredTasks([]);
      }
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/employee/tasks/${taskId}/status`, { status: newStatus });
      fetchTasks(currentPage);
    } catch (err) {
      console.error('Error updating task status:', err);
      alert('Failed to update task status. Please try again.');
    }
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    fetchTasks(1, newFilters);
  };

  const clearFilters = () => {
    const reset = { status: '', priority: '', project: '' };
    setFilters(reset);
    fetchTasks(1, reset);
  };

  const getUniqueProjects = () => {
    return [...new Set(tasks.filter(task => task.Project).map(task => task.Project.name))];
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'todo': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const fetchProjects = async (search = '') => {
    try {
      const response = await api.post('/projects/list', { search: search || undefined, limit: 50, page: 1 });
      setProjects(response.data.projects || response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', { params: { limit: 1000 } });
      setUsers(response.data.users || response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const taskData = {
        ...newTask,
        project_id: modalSelectedProject ? parseInt(modalSelectedProject.id) : null,
        assigned_users: modalSelectedUsers.length > 0 ? modalSelectedUsers.map(u => u.id) : [user.id]
      };
      await api.post('/tasks', taskData);
      setShowCreateModal(false);
      setNewTask({ title: '', description: '', priority: 'medium', project_id: '', assigned_users: [], due_date: '' });
      setModalSelectedProject(null);
      setModalProjectSearch('');
      setModalSelectedUsers([]);
      setModalUserSearch('');
      fetchTasks(1);
      alert('Task created successfully!');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const handleTaskClick = async (task) => {
    setSelectedTask(task);
    await fetchTaskComments(task.id);
  };

  const fetchTaskComments = async (taskId) => {
    try {
      setLoadingComments(true);
      const response = await api.get(`/employee/tasks/${taskId}/comments`);
      setTaskComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && selectedImages.length === 0) return;
    
    try {
      setUploadingComment(true);
      const formData = new FormData();
      formData.append('content', newComment);
      
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });
      
      const response = await api.post(`/tasks/${selectedTask.id}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTaskComments([...taskComments, response.data]);
      setNewComment('');
      setSelectedImages([]);
      
      setTimeout(() => {
        const commentsContainer = document.querySelector('.comments-scroll-container');
        if (commentsContainer) {
          commentsContainer.scrollTop = commentsContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setUploadingComment(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter(file => 
      ['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)
    );
    setSelectedImages(prev => [...prev, ...validImages]);
  };
  
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      await api.put(`/employee/tasks/${taskId}/status`, { status: newStatus });
      setSelectedTask({...selectedTask, status: newStatus});
      const updatedTasks = tasks.map(t => 
        t.id === taskId ? {...t, status: newStatus} : t
      );
      setTasks(updatedTasks);
      setFilteredTasks(updatedTasks);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">My Tasks</h1>
        <div className="text-center py-8">Loading tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">My Tasks</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchTasks} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">My Tasks</h1>

        <div className="flex items-center space-x-4">
          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              Table View
            </button>
            <button
              onClick={() => setViewMode('project')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'project' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              Project View
            </button>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status (Active)</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="testing">Testing</option>
              <option value="client_review">Client Review</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <input
              type="text"
              value={projectSearch}
              onChange={(e) => { setProjectSearch(e.target.value); setProjectDropdownOpen(true); fetchProjects(e.target.value); }}
              onFocus={() => { setProjectDropdownOpen(true); fetchProjects(projectSearch); }}
              onBlur={() => setTimeout(() => setProjectDropdownOpen(false), 200)}
              placeholder="Search project..."
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-48"
            />
            {projectDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                <button
                  type="button"
                  onMouseDown={() => { handleFilterChange('project', ''); setProjectSearch(''); setProjectDropdownOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.project === '' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  All Projects
                </button>
                {projects.map(project => (
                  <button
                    key={project.id}
                    type="button"
                    onMouseDown={() => { handleFilterChange('project', project.name); setProjectSearch(project.name); setProjectDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.project === project.name ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    {project.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">No tasks assigned to you yet</p>
        </div>
      ) : viewMode === 'table' ? (
        /* Monday.com Style Table View */
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 w-80">Task</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Status</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Priority</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Due Date</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-40">Project</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-gray-900 w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTasks.map((task, index) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleTaskClick(task)}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors text-left"
                          >
                            {task.title}
                          </button>
                          {task.description && (
                            <>
                              <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                                {task.description.replace(/<[^>]*>/g, ' ').trim().slice(0, 50) + (task.description.replace(/<[^>]*>/g, '').length > 50 ? '...' : '')}
                              </p>
                              {task.description.replace(/<[^>]*>/g, '').length > 50 && (
                                <button onClick={(e) => { e.stopPropagation(); setDescPopup(task); }} className="text-xs text-blue-600 hover:underline">Read more</button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <select
                        value={task.status}
                        onChange={(e) => handleUpdateStatus(task.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(task.status)} text-white`}
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="testing">Testing</option>
                        <option value="client_review">Client Review</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)} text-white`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm text-gray-600">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                        {task.Project?.name || 'No Project'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => handleTaskClick(task)} className="text-blue-500 hover:text-blue-700 transition-colors">
                          <FiEye size={16} />
                        </button>
                        <button onClick={() => handleTaskClick(task)} className="text-green-500 hover:text-green-700 transition-colors">
                          <FiMessageSquare size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Project View */
        <div className="space-y-6">
          {Object.entries(
            filteredTasks.reduce((groups, task) => {
              const projectName = task.Project?.name || 'No Project';
              if (!groups[projectName]) groups[projectName] = [];
              groups[projectName].push(task);
              return groups;
            }, {})
          ).map(([projectName, projectTasks]) => (
            <div key={projectName} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">{projectName}</h3>
                <p className="text-sm text-gray-500">{projectTasks.length} tasks</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projectTasks.map((task) => (
                      <tr key={task.id}>
                        <td className="px-6 py-4">
                          <Link 
                            to={`/employee/tasks/${task.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                          >
                            {task.title}
                          </Link>
                          {task.description && (
                            <>
                              <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                                {task.description.replace(/<[^>]*>/g, ' ').trim().slice(0, 50) + (task.description.replace(/<[^>]*>/g, '').length > 50 ? '...' : '')}
                              </div>
                              {task.description.replace(/<[^>]*>/g, '').length > 50 && (
                                <button onClick={(e) => { e.stopPropagation(); setDescPopup(task); }} className="text-xs text-blue-600 hover:underline">Read more</button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full text-white ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={task.status}
                            onChange={(e) => handleUpdateStatus(task.id, e.target.value)}
                            className="text-sm border-gray-300 rounded-md"
                          >
                            <option value="todo">To Do</option>
                            <option value="in_progress">In Progress</option>
                            <option value="testing">Testing</option>
                            <option value="client_review">Client Review</option>
                            <option value="completed">Completed</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(task.due_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.Client ? task.Client.name : 'No client'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Link to={`/employee/tasks/${task.id}`} className="text-blue-600 hover:text-blue-900" title="View Task">
                              <FiEye />
                            </Link>
                            <Link to={`/employee/tasks/${task.id}`} className="text-green-600 hover:text-green-900" title="View Comments">
                              <FiMessageSquare />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
          <p className="text-sm text-gray-500">
            Showing {(currentPage - 1) * LIMIT + 1}–{Math.min(currentPage * LIMIT, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => fetchTasks(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => fetchTasks(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  page === currentPage ? 'bg-blue-500 text-white border-blue-500' : 'hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => fetchTasks(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Create New Task</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <ReactQuill
                  theme="snow"
                  value={newTask.description}
                  onChange={(value) => setNewTask({...newTask, description: value})}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ height: '160px', marginBottom: '42px' }}
                  placeholder="Enter task description..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <div className="relative">
                  <input
                    type="text"
                    value={modalProjectSearch}
                    onChange={e => { setModalProjectSearch(e.target.value); setModalProjectOpen(true); fetchProjects(e.target.value); }}
                    onFocus={() => { setModalProjectOpen(true); fetchProjects(modalProjectSearch); }}
                    placeholder="Search project..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    autoComplete="off"
                  />
                  {modalSelectedProject && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">{modalSelectedProject.name}</span>
                      <button type="button" onClick={() => { setModalSelectedProject(null); setModalProjectSearch(''); }} className="text-xs text-gray-400 hover:text-red-500">&times;</button>
                    </div>
                  )}
                  {modalProjectOpen && !modalSelectedProject && projects.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto mt-1">
                      {projects.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setModalSelectedProject(p); setModalProjectSearch(p.name); setModalProjectOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                {modalSelectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {modalSelectedUsers.map(u => (
                      <span key={u.id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                        {u.name}
                        <button type="button" onClick={() => setModalSelectedUsers(modalSelectedUsers.filter(x => x.id !== u.id))} className="hover:text-red-600">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  type="text"
                  value={modalUserSearch}
                  onChange={e => setModalUserSearch(e.target.value)}
                  placeholder="Search users to assign..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                {modalUserSearch && (
                  <div className="border border-gray-200 rounded-md max-h-32 overflow-y-auto mt-1">
                    {users.filter(u =>
                      u.name.toLowerCase().includes(modalUserSearch.toLowerCase()) &&
                      !modalSelectedUsers.find(s => s.id === u.id)
                    ).map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => { setModalSelectedUsers([...modalSelectedUsers, u]); setModalUserSearch(''); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Description Popup */}
      {descPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDescPopup(null)}>
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{descPopup.title}</h2>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {descPopup.Project && <span>Project: <span className="font-medium text-gray-700">{descPopup.Project.name}</span></span>}
                  {descPopup.Client && <span>Client: <span className="font-medium text-gray-700">{descPopup.Client.name}</span></span>}
                  <span>Priority: <span className="font-medium text-gray-700 capitalize">{descPopup.priority}</span></span>
                </div>
              </div>
              <button onClick={() => setDescPopup(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto px-8 py-6">
              <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: descPopup.description }} />
            </div>
          </div>
        </div>
      )}
      {selectedTask && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setSelectedTask(null)}></div>
          <div className="absolute right-0 top-0 h-full w-full md:w-[500px] bg-white shadow-xl">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">{selectedTask.title}</h2>
                  <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600">
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Task Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Description</h3>
                  <div className="prose prose-sm max-w-none text-sm text-gray-600">
                    {selectedTask.description
                      ? <div dangerouslySetInnerHTML={{ __html: selectedTask.description }} />
                      : 'No description'
                    }
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Status</h3>
                  <select
                    value={selectedTask.status}
                    onChange={(e) => handleTaskStatusUpdate(selectedTask.id, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="testing">Testing</option>
                    <option value="client_review">Client Review</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Task Info */}
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Priority: </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getPriorityColor(selectedTask.priority)}`}>
                      {selectedTask.priority}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Due Date: </span>
                    <span className="text-sm text-gray-600">{formatDate(selectedTask.due_date)}</span>
                  </div>
                  {selectedTask.Project && (
                    <div>
                      <span className="text-sm font-medium text-gray-900">Project: </span>
                      <span className="text-sm text-gray-600">{selectedTask.Project.name}</span>
                    </div>
                  )}
                  {selectedTask.Client && (
                    <div>
                      <span className="text-sm font-medium text-gray-900">Client: </span>
                      <span className="text-sm text-gray-600">{selectedTask.Client.name}</span>
                    </div>
                  )}
                </div>

                {/* Comments Section */}
                <div className="flex flex-col h-96">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Comments</h3>
                  
                  {/* Comments List - Scrollable */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2 comments-scroll-container">
                    {loadingComments ? (
                      <p className="text-sm text-gray-500">Loading comments...</p>
                    ) : taskComments.length > 0 ? (
                      taskComments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 rounded-md p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{comment.User?.name || 'Unknown'}</span>
                            <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                          </div>
                          {comment.content && (
                            <p className="text-sm text-gray-600 mb-2" style={{whiteSpace: 'pre-wrap'}}>{comment.content}</p>
                          )}
                          {comment.images && (
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                              {(() => {
                                try {
                                  return JSON.parse(comment.images).map((imageUrl, imgIndex) => (
                                    <img
                                      key={imgIndex}
                                      src={`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`}
                                      alt={`Comment image ${imgIndex + 1}`}
                                      className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => window.open(`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`, '_blank')}
                                    />
                                  ));
                                } catch (e) {
                                  console.error('Error parsing images:', e);
                                  return null;
                                }
                              })()
                              }
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No comments yet</p>
                    )}
                  </div>

                  {/* Add Comment - Fixed at Bottom */}
                  <div className="border-t pt-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="3"
                    />
                    
                    {/* Image Upload */}
                    <div className="mt-2 mb-3">
                      <input
                        type="file"
                        multiple
                        accept=".png,.jpg,.jpeg"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50"
                      >
                        <FiImage className="mr-1" size={14} />
                        Attach Images
                      </label>
                      
                      {/* Selected Images Preview */}
                      {selectedImages.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {selectedImages.map((image, index) => (
                            <div key={index} className="relative">
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-16 object-cover rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <FiX size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Add Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddComment}
                        disabled={(!newComment.trim() && selectedImages.length === 0) || uploadingComment}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 flex items-center"
                      >
                        {uploadingComment ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                            Adding...
                          </>
                        ) : (
                          'Add Comment'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTasksPage;